import React, { useCallback, useRef, useState } from "react";
import { Loader } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Narrowest a column may be dragged. Callers that size this layout's container
 * keep it at twice this width, so neither column is ever forced below it.
 */
export const MIN_COLUMN_WIDTH_PX = 340;

/** Floor (%) used before the group is measured. */
const DEFAULT_MIN_PANEL_PERCENT = 20;

/**
 * Props for the TwoColumnLayout component.
 * Provides a two-column layout with a back-button header and optional footer,
 * sized to fill its container.
 */
export interface TwoColumnLayoutProps {
  /** Called when the back button is pressed. */
  onClose: () => void;
  header?: React.ReactNode;
  leftColumn: React.ReactNode;
  rightColumn: React.ReactNode;
  footer?: React.ReactNode;
  /** Optional panel rendered to the left of the two-column grid (e.g. assembly tree sidebar). */
  sidePanel?: React.ReactNode;
  leftColumnSize?: "quarter" | "third" | "half" | "full";
  /** When true, renders a draggable resize handle between the left and right columns. */
  resizable?: boolean;
  /** Unique key for persisting the resizable panel sizes to localStorage. */
  autoSaveId?: string;
  /** When true, displays a centered loading indicator instead of the two-column layout */
  loading?: boolean;
  /** Custom loading text to display below the spinner */
  loadingText?: string;
}

/**
 * Two-column layout with header, footer, and optional resizable columns.
 * Fills its parent container, so it works both inside a full-screen sheet
 * and inline within a panel.
 */
export const TwoColumnLayout: React.FC<TwoColumnLayoutProps> = ({
  onClose,
  header,
  leftColumn,
  rightColumn,
  footer,
  sidePanel,
  leftColumnSize = "half",
  resizable = false,
  autoSaveId,
  loading = false,
  loadingText = "Loading...",
}) => {
  const contentHeight = `calc(100%${header ? " - 60px" : ""}${
    footer && !loading ? " - 60px" : ""
  })`;

  const getDefaultSizePercent = (): number => {
    switch (leftColumnSize) {
      case "quarter":
        return 25;
      case "third":
        return 33;
      case "half":
      default:
        return 50;
    }
  };

  const getGridClasses = () => {
    switch (leftColumnSize) {
      case "full":
        return "border-b grid grid-cols-1 gap-0 flex-grow w-full";
      case "quarter":
        return "border-b grid grid-cols-1 md:grid-cols-4 gap-0 flex-grow w-full";
      case "third":
        return "border-b grid grid-cols-1 md:grid-cols-3 gap-0 flex-grow w-full";
      case "half":
      default:
        return "border-b grid grid-cols-1 md:grid-cols-2 gap-0 flex-grow w-full";
    }
  };

  const getLeftColumnClasses = () => {
    if (leftColumnSize === "full") {
      return "bg-muted/30 flex items-center justify-center overflow-y-auto h-full w-full";
    }
    return "md:col-span-1 bg-muted/30 flex items-center justify-center overflow-y-auto h-full w-full";
  };

  const getRightColumnClasses = () => {
    switch (leftColumnSize) {
      case "full":
        return "hidden";
      case "quarter":
        return "md:col-span-3 overflow-y-auto h-full w-full";
      case "third":
        return "md:col-span-2 overflow-y-auto h-full w-full";
      case "half":
      default:
        return "md:col-span-1 overflow-y-auto h-full w-full";
    }
  };

  const columnsObserverRef = useRef<ResizeObserver | null>(null);
  const [columnsWidth, setColumnsWidth] = useState(0);

  /**
   * Measures the box the two columns share, so the split follows the container.
   * React calls this with null on unmount, which disconnects the observer.
   */
  const attachColumns = useCallback((node: HTMLDivElement | null) => {
    columnsObserverRef.current?.disconnect();
    if (!node) {
      return;
    }
    const observer = new ResizeObserver(([entry]) =>
      setColumnsWidth(entry.contentRect.width),
    );
    observer.observe(node);
    columnsObserverRef.current = observer;
  }, []);

  // A touch device gets no drag handle, independent of how wide it is.
  const isMobile = useIsMobile();
  const useResizable = resizable && leftColumnSize !== "full" && !isMobile;

  /**
   * react-resizable-panels v2 takes a percentage, not pixels. Capped at half so
   * a container too narrow for two full columns still resolves — the handle
   * locks at centre rather than starving one side.
   */
  const minPanelPercent =
    columnsWidth > 0
      ? Math.min(50, (MIN_COLUMN_WIDTH_PX / columnsWidth) * 100)
      : DEFAULT_MIN_PANEL_PERCENT;

  return (
    <div className="w-full h-full max-w-full p-0 flex flex-col">
      <div className="border-b px-4 py-3 bg-background h-[60px] flex items-center">
        <BackButton className="mr-3 -ml-1" onClick={onClose} />
        {header}
      </div>
      {loading ? (
        <div
          className="flex items-center justify-center w-full"
          style={{ height: contentHeight }}
        >
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader className="h-5 w-5 animate-spin" />
            <span>{loadingText}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-row" style={{ height: contentHeight }}>
            {sidePanel}
            {useResizable ? (
              <div ref={attachColumns} className="flex flex-1 min-w-0">
                <ResizablePanelGroup
                  direction="horizontal"
                  autoSaveId={autoSaveId}
                  className="border-b flex-1 min-w-0"
                >
                  {/* Explicit ids keep the saved layout keyed by panel rather
                      than by constraints, which now vary with the width. */}
                  <ResizablePanel
                    id="left"
                    order={1}
                    defaultSize={getDefaultSizePercent()}
                    minSize={minPanelPercent}
                  >
                    <div className="bg-muted/30 flex items-center justify-center overflow-y-auto h-full w-full">
                      {leftColumn}
                    </div>
                  </ResizablePanel>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    id="right"
                    order={2}
                    minSize={minPanelPercent}
                  >
                    <div className="overflow-y-auto h-full w-full">
                      {rightColumn}
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              </div>
            ) : (
              <div className={`${getGridClasses()} flex-1 min-w-0`}>
                <div className={getLeftColumnClasses()}>{leftColumn}</div>
                <div className={getRightColumnClasses()}>{rightColumn}</div>
              </div>
            )}
          </div>
          {footer && (
            <div className="border-b px-4 py-3 flex justify-end bg-background h-[60px]">
              {footer}
            </div>
          )}
        </>
      )}
    </div>
  );
};
